<script setup>
import { computed, ref, watch } from 'vue'
import { Badge, Button, Spinner } from '@drama/ui'
import { ExternalLink, X } from '@lucide/vue'
import { useI18n } from 'vue-i18n'

import LongText from '../../../components/LongText.vue'
import { getLlmConversation } from '../../../api/llm/index.js'
import ConversationCallTimeline from '../conversation/ConversationCallTimeline.vue'
import VideoMatchLogs from '../video-match/VideoMatchLogs.vue'
import RunVideoMatchResourceCard from './RunVideoMatchResourceCard.vue'
import { stepExecutionDetail } from './run-video-match-step-detail.js'

const props = defineProps({
  selected: { type: Object, required: true },
  selectedNodeId: { type: String, required: true },
  resources: { type: Array, required: true },
  runId: { type: String, required: true },
})
defineEmits(['close'])

const { t, te } = useI18n()
const tab = ref('details')
const agentCallsLoading = ref(false)
const agentCallsLoaded = ref(false)
const agentCallsError = ref('')
const agentConversations = ref([])
const turn = computed(() => props.selected.turn)
const step = computed(() => props.selected.kind === 'step' ? props.selected.step : null)
const resourcesById = computed(() => new Map(props.resources.map(resource => [resource.id, resource])))
const executionDetail = computed(() => stepExecutionDetail({
  step: step.value,
  stepIndex: props.selected.step_index,
  turn: turn.value,
  t,
}))
const nodeLogs = computed(() => logsForSelection(props.selected, turn.value.logs))
const showsEmbeddingLogs = computed(() => (
  props.selected.kind === 'turn' || isEmbeddingStep(step.value.kind)
))
const showsModelDecisionLogs = computed(() => (
  props.selected.kind === 'turn' || isModelDecisionStep(step.value.kind)
))
const hasNodeLogs = computed(() => (
  nodeLogs.value.embedding_calls.length > 0 || nodeLogs.value.model_decision_calls.length > 0
))

watch(() => props.selectedNodeId, () => {
  tab.value = 'details'
})

watch(() => turn.value.context.turn_id, () => {
  agentCallsLoading.value = false
  agentCallsLoaded.value = false
  agentCallsError.value = ''
  agentConversations.value = []
})

function has(value, field) {
  return Object.hasOwn(value, field)
}

function resource(resourceId) {
  const item = resourcesById.value.get(resourceId)
  if (item === undefined) throw new Error(`Missing video resource in run match detail: ${resourceId}`)
  return item
}

function methodLabel(method) {
  switch (method) {
    case 'exact_transition':
    case 'semantic_reuse':
    case 'intent_video':
      return t(`visualGameAdmin.llm.videoMatch.methods.${method}`)
    case null:
      return t('visualGameAdmin.llm.videoMatch.methods.none')
    default:
      return method
  }
}

function statusVariant(status) {
  switch (status) {
    case 'passed':
    case 'ok':
    case 'available':
    case 'selected':
      return 'success'
    case 'error':
      return 'danger'
    case 'rejected':
    case 'needs_llm_selection':
    case 'stopped':
      return 'warning'
    case 'empty':
    case 'below_minimum':
    case 'miss':
    case 'not_recorded':
    case 'uncovered':
      return 'neutral'
    default:
      return 'neutral'
  }
}

function callCapability(kind) {
  switch (kind) {
    case 'semantic_rerank':
    case 'intent_llm_selection':
      return 'text'
    case 'semantic_embedding':
    case 'intent_embedding':
    case 'intent_demand_embedding':
      return 'embedding'
    default:
      return null
  }
}

function filterLabel(key) {
  switch (key) {
    case 'mode':
    case 'intent':
    case 'versionId':
    case 'locationId':
    case 'embeddingModelId':
    case 'semanticTemplateVersion':
      return t(`visualGameAdmin.llm.runVideoMatch.filters.${key}`)
    default:
      return key
  }
}

function isSelectedCandidate(candidate) {
  return has(step.value, 'selected_resource_id')
    && step.value.selected_resource_id === candidate.resource_id
}

function isEmbeddingStep(kind) {
  switch (kind) {
    case 'semantic_embedding':
    case 'intent_embedding':
    case 'intent_demand_embedding':
      return true
    case 'cg_recall_gate':
    case 'smart_match_setting':
    case 'exact_match':
    case 'saved_semantic_match':
    case 'semantic_candidate_bucket':
    case 'semantic_vector_search':
    case 'semantic_rerank':
    case 'intent_llm_selection':
    case 'intent_vector_search':
    case 'result':
    case 'historical_capture':
      return false
    default:
      return false
  }
}

function logsForSelection(selected, logs) {
  if (selected.kind === 'turn') return logs
  const selectedStep = selected.step
  switch (selectedStep.kind) {
    case 'semantic_embedding':
    case 'intent_embedding':
    case 'intent_demand_embedding':
      return {
        agent_calls: [],
        embedding_calls: logs.embedding_calls.filter(call => selectedStep.status === 'ok'
          ? call.id === selectedStep.call_id
          : call.operation_id === embeddingOperation(selectedStep.kind, turn.value.context.turn_id)),
        model_decision_calls: [],
      }
    case 'semantic_rerank':
    case 'intent_llm_selection':
      return {
        agent_calls: [],
        embedding_calls: [],
        model_decision_calls: logs.model_decision_calls.filter(call => selectedStep.status === 'error'
          ? call.billing_operation_id === modelDecisionOperation(selectedStep.kind, turn.value.context.turn_id)
          : call.request_id === selectedStep.call_id),
      }
    case 'historical_capture':
    case 'cg_recall_gate':
    case 'smart_match_setting':
    case 'exact_match':
    case 'saved_semantic_match':
    case 'semantic_candidate_bucket':
    case 'semantic_vector_search':
    case 'intent_vector_search':
    case 'result':
      return { agent_calls: [], embedding_calls: [], model_decision_calls: [] }
    default:
      return { agent_calls: [], embedding_calls: [], model_decision_calls: [] }
  }
}

function stepLabel(kind) {
  const key = `visualGameAdmin.llm.videoMatch.steps.${kind}`
  return te(key) ? t(key) : kind
}

function statusLabel(status) {
  const key = `visualGameAdmin.llm.videoMatch.status.${status}`
  return te(key) ? t(key) : status
}

function isModelDecisionStep(kind) {
  return kind === 'semantic_rerank' || kind === 'intent_llm_selection'
}

function modelDecisionOperation(kind, turnId) {
  switch (kind) {
    case 'semantic_rerank':
      return `video_resource_rerank:${turnId}`
    case 'intent_llm_selection':
      return `intent_video_selection:${turnId}`
    default:
      return null
  }
}

function embeddingOperation(kind, turnId) {
  switch (kind) {
    case 'semantic_embedding':
      return `video_resource_match:${turnId}`
    case 'intent_embedding':
      return `intent_recall:video_resource_match:${turnId}`
    case 'intent_demand_embedding':
      return `intent_demand:${turnId}`
    default:
      return null
  }
}

function embeddingInput(kind) {
  const input = turn.value.embedding_inputs[kind]
  if (input === undefined) {
    throw new Error(`Missing embedding input: turn ${turn.value.context.turn_id}, step ${kind}`)
  }
  return input
}

function embeddingCall() {
  const call = turn.value.logs.embedding_calls.find(item => item.id === step.value.call_id)
  if (call === undefined) {
    throw new Error(`Missing embedding call: turn ${turn.value.context.turn_id}, call ${step.value.call_id}`)
  }
  return call
}

async function openLogs() {
  tab.value = 'logs'
  if (props.selected.kind === 'step') return
  if (agentCallsLoaded.value || agentCallsLoading.value) return
  if (turn.value.logs.agent_calls.length === 0) {
    agentCallsLoaded.value = true
    return
  }
  agentCallsLoading.value = true
  agentCallsError.value = ''
  try {
    const details = await Promise.all(turn.value.context.conversation_keys.map(
      conversationKey => getLlmConversation(props.runId, conversationKey),
    ))
    agentConversations.value = details.map(detail => {
      const round = detail.rounds.find(item => item.turn_id === turn.value.context.turn_id)
      if (round === undefined) {
        throw new Error(
          `Conversation ${detail.conversation.conversation_key} does not contain turn ${turn.value.context.turn_id}`,
        )
      }
      return {
        conversation_key: detail.conversation.conversation_key,
        agent_role: detail.conversation.agent_role,
        calls: round.calls,
      }
    })
    agentCallsLoaded.value = true
  } catch (requestError) {
    agentCallsError.value = requestError.message
  } finally {
    agentCallsLoading.value = false
  }
}
</script>

<template>
  <aside class="run-match-inspector">
    <header class="run-match-inspector__head">
      <div>
        <span>{{ t('visualGameAdmin.llm.runVideoMatch.inspector') }}</span>
        <h2 v-if="selected.kind === 'turn'">
          {{ t('visualGameAdmin.llm.runVideoMatch.turnNumber', { number: turn.context.turn_no }) }}
        </h2>
        <h2 v-else>{{ stepLabel(step.kind) }}</h2>
      </div>
      <Button variant="ghost" size="sm" :aria-label="t('visualGameAdmin.common.close')" @click="$emit('close')">
        <X :size="17" />
      </Button>
    </header>

    <nav class="run-match-inspector__tabs">
      <button type="button" :class="{ 'is-active': tab === 'details' }" @click="tab = 'details'">
        {{ t('visualGameAdmin.llm.runVideoMatch.details') }}
      </button>
      <button type="button" :class="{ 'is-active': tab === 'logs' }" @click="openLogs">
        {{ t('visualGameAdmin.llm.videoMatch.logsTitle') }}
      </button>
    </nav>

    <div v-if="tab === 'details'" class="run-match-inspector__body">
      <template v-if="selected.kind === 'turn'">
        <LongText
          :label="t('visualGameAdmin.llm.runVideoMatch.playerInput')"
          :text="turn.context.input"
          :copy-text="turn.context.input"
          :collapse-over="400"
          markdown
        />
        <dl class="run-match-inspector__facts">
          <div>
            <dt>{{ t('visualGameAdmin.llm.runVideoMatch.resultMethod') }}</dt>
            <dd>{{ methodLabel(turn.context.match_method) }}</dd>
          </div>
          <div>
            <dt>{{ t('visualGameAdmin.llm.videoMatch.result.similarity') }}</dt>
            <dd>{{ turn.context.match_similarity === null ? '—' : turn.context.match_similarity.toFixed(4) }}</dd>
          </div>
          <div>
            <dt>{{ t('visualGameAdmin.llm.videoMatch.result.resource') }}</dt>
            <dd><code>{{ turn.context.resource_id === null ? '—' : turn.context.resource_id }}</code></dd>
          </div>
          <div>
            <dt>{{ t('visualGameAdmin.llm.runVideoMatch.traceCapture') }}</dt>
            <dd>{{ t(`visualGameAdmin.llm.videoMatch.capture.${turn.trace.capture}`) }}</dd>
          </div>
        </dl>
        <section v-if="turn.context.match_reason !== null" class="run-match-inspector__decision">
          <h3>{{ t('visualGameAdmin.llm.runVideoMatch.finalDecisionReason') }}</h3>
          <p>{{ turn.context.match_reason }}</p>
        </section>
        <section class="run-match-inspector__conversations">
          <h3>{{ t('visualGameAdmin.llm.runVideoMatch.relatedConversations') }}</h3>
          <RouterLink
            v-for="conversationKey in turn.context.conversation_keys"
            :key="conversationKey"
            :to="{
              name: 'llm-conversations',
              query: { run_id: runId, conversation_key: conversationKey, turn_id: turn.context.turn_id },
            }"
          >
            {{ conversationKey }}
            <ExternalLink :size="14" />
          </RouterLink>
        </section>
      </template>

      <template v-else>
        <div class="run-match-inspector__status">
          <Badge :variant="statusVariant(step.status)">
            {{ statusLabel(step.status) }}
          </Badge>
          <span>{{ t('visualGameAdmin.llm.videoMatch.stepNumber', { number: selected.step_index + 1 }) }}</span>
        </div>

        <section class="run-match-inspector__execution">
          <h3>{{ t('visualGameAdmin.llm.runVideoMatch.execution.title') }}</h3>
          <p>{{ executionDetail.summary }}</p>
          <dl v-if="executionDetail.facts.length > 0" class="run-match-inspector__facts">
            <div v-for="item in executionDetail.facts" :key="item.label">
              <dt>{{ item.label }}</dt>
              <dd><code v-if="item.code">{{ item.value }}</code><template v-else>{{ item.value }}</template></dd>
            </div>
          </dl>
        </section>

        <section v-if="has(step, 'reason')" class="run-match-inspector__decision">
          <h3>
            {{ t(isModelDecisionStep(step.kind)
              ? 'visualGameAdmin.llm.runVideoMatch.modelDecisionReason'
              : 'visualGameAdmin.llm.runVideoMatch.stepRecordedReason') }}
          </h3>
          <p>{{ step.reason }}</p>
        </section>

        <VideoMatchLogs
          v-if="isModelDecisionStep(step.kind) && hasNodeLogs"
          :logs="nodeLogs"
          compact
          :show-embedding="false"
          :show-model-decisions="true"
          :show-agent-calls="false"
          expand-model-decisions
        />

        <dl v-if="has(step, 'filter')" class="run-match-inspector__facts">
          <div v-for="(value, key) in step.filter" :key="key">
            <dt>{{ filterLabel(key) }}</dt>
            <dd><code>{{ value }}</code></dd>
          </div>
        </dl>

        <RouterLink
          v-if="has(step, 'call_id') && callCapability(step.kind) !== null && !isModelDecisionStep(step.kind)"
          class="run-match-inspector__link"
          :to="{ name: 'llm-calls', query: { capability: callCapability(step.kind), request_id: step.call_id } }"
          target="_blank"
          rel="noopener noreferrer"
        >
          {{ t('visualGameAdmin.llm.videoMatch.openCall', { id: step.call_id }) }}
          <ExternalLink :size="14" />
        </RouterLink>

        <section v-if="isEmbeddingStep(step.kind) && step.status === 'ok'" class="run-match-inspector__embedding">
          <LongText
            :label="t('visualGameAdmin.llm.runVideoMatch.embeddingInput')"
            :text="embeddingInput(step.kind)"
            :copy-text="embeddingInput(step.kind)"
            :collapse-over="600"
            markdown
          />
          <div>
            <h3>{{ t('visualGameAdmin.llm.runVideoMatch.embeddingOutput') }}</h3>
            <dl class="run-match-inspector__facts">
              <div>
                <dt>{{ t('visualGameAdmin.llm.videoMatch.logs.callId') }}</dt>
                <dd><code>{{ embeddingCall().id }}</code></dd>
              </div>
              <div>
                <dt>{{ t('visualGameAdmin.llm.videoMatch.logs.dimensions') }}</dt>
                <dd>{{ embeddingCall().dimensions }}</dd>
              </div>
              <div>
                <dt>{{ t('visualGameAdmin.llm.runVideoMatch.providerModel') }}</dt>
                <dd>{{ embeddingCall().provider }} · {{ embeddingCall().model }}</dd>
              </div>
              <div>
                <dt>{{ t('visualGameAdmin.llm.videoMatch.logs.inputHash') }}</dt>
                <dd><code>{{ embeddingCall().input_hash }}</code></dd>
              </div>
            </dl>
            <p>{{ t('visualGameAdmin.llm.runVideoMatch.vectorStorageNote') }}</p>
          </div>
        </section>

        <div v-if="has(step, 'candidates')" class="run-match-inspector__candidates">
          <RunVideoMatchResourceCard
            v-for="(candidate, index) in step.candidates"
            :key="candidate.resource_id"
            :resource="resource(candidate.resource_id)"
            :rank="index + 1"
            :similarity="candidate.similarity"
            :threshold="has(step, 'minimum_similarity_threshold') ? step.minimum_similarity_threshold : null"
            :selected="isSelectedCandidate(candidate)"
          />
        </div>

        <RunVideoMatchResourceCard
          v-else-if="has(step, 'resource_id') && step.resource_id !== null"
          :resource="resource(step.resource_id)"
          selected
        />
      </template>
    </div>

    <div v-else class="run-match-inspector__logs">
      <VideoMatchLogs
        v-if="selected.kind === 'turn' || hasNodeLogs"
        :logs="nodeLogs"
        :show-embedding="showsEmbeddingLogs"
        :show-model-decisions="showsModelDecisionLogs"
        :show-agent-calls="false"
      />
      <p v-else class="run-match-inspector__no-node-logs">
        {{ t('visualGameAdmin.llm.runVideoMatch.noNodeExternalCall') }}
      </p>

      <section v-if="selected.kind === 'turn'" class="run-match-inspector__agent-calls">
        <header>
          <div>
            <h3>{{ t('visualGameAdmin.llm.runVideoMatch.agentCalls') }}</h3>
            <p>{{ t('visualGameAdmin.llm.runVideoMatch.agentCallsDescription') }}</p>
          </div>
          <Badge variant="neutral" size="sm">{{ turn.logs.agent_calls.length }}</Badge>
        </header>
        <Spinner
          v-if="agentCallsLoading"
          :label="t('visualGameAdmin.llm.runVideoMatch.loadingAgentCalls')"
        />
        <p v-else-if="agentCallsError !== ''" class="vga-error" role="alert">{{ agentCallsError }}</p>
        <p v-else-if="turn.logs.agent_calls.length === 0" class="run-match-inspector__empty">
          {{ t('visualGameAdmin.llm.runVideoMatch.noAgentCalls') }}
        </p>
        <article
          v-for="conversation in agentConversations"
          v-else
          :key="conversation.conversation_key"
          class="run-match-inspector__agent-conversation"
        >
          <header>
            <strong>{{ conversation.agent_role }}</strong>
            <code>{{ conversation.conversation_key }}</code>
          </header>
          <ConversationCallTimeline :calls="conversation.calls" request-open />
        </article>
      </section>
    </div>
  </aside>
</template>

<style scoped>
.run-match-inspector {
  display: grid;
  align-content: start;
  gap: var(--drama-space-lg);
  width: 100%;
  padding: var(--drama-space-xl);
  border-top: 1px solid var(--drama-color-border);
  background: var(--drama-color-surface);
}

.run-match-inspector__head,
.run-match-inspector__status {
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: var(--drama-space-md);
}

.run-match-inspector__head span {
  color: var(--drama-color-text-muted);
  font-size: var(--drama-font-size-xs);
}

.run-match-inspector__head h2,
.run-match-inspector__conversations h3 {
  margin: var(--drama-space-xs) 0 0;
}

.run-match-inspector__tabs {
  display: flex;
  gap: var(--drama-space-xs);
  border-bottom: 1px solid var(--drama-color-border);
}

.run-match-inspector__tabs button {
  padding: var(--drama-space-sm) var(--drama-space-md);
  border: 0;
  border-bottom: 2px solid transparent;
  background: transparent;
  color: var(--drama-color-text-muted);
  cursor: pointer;
}

.run-match-inspector__tabs button:hover,
.run-match-inspector__tabs button.is-active {
  color: var(--drama-color-primary);
}

.run-match-inspector__tabs button.is-active {
  border-bottom-color: var(--drama-color-primary);
}

.run-match-inspector__tabs button:focus-visible {
  outline: 2px solid var(--drama-color-primary);
  outline-offset: 2px;
}

.run-match-inspector__body,
.run-match-inspector__conversations,
.run-match-inspector__candidates,
.run-match-inspector__decision,
.run-match-inspector__execution,
.run-match-inspector__embedding,
.run-match-inspector__logs,
.run-match-inspector__agent-calls,
.run-match-inspector__agent-conversation {
  display: grid;
  gap: var(--drama-space-md);
}

.run-match-inspector__body {
  gap: var(--drama-space-lg);
}

.run-match-inspector__facts {
  display: grid;
  grid-template-columns: repeat(2, minmax(0, 1fr));
  gap: var(--drama-space-md);
  margin: 0;
}

.run-match-inspector__facts div {
  min-width: 0;
}

.run-match-inspector__facts dt {
  color: var(--drama-color-text-muted);
  font-size: var(--drama-font-size-xs);
}

.run-match-inspector__facts dd {
  margin: var(--drama-space-xs) 0 0;
  overflow-wrap: anywhere;
}

.run-match-inspector__threshold {
  margin: 0;
  padding: var(--drama-space-md);
  border-radius: var(--drama-radius-md);
  background: var(--drama-color-surface-alt);
  line-height: 1.6;
}

.run-match-inspector__decision {
  padding: var(--drama-space-md);
  border-radius: var(--drama-radius-md);
  background: var(--drama-color-surface-alt);
}

.run-match-inspector__decision h3,
.run-match-inspector__decision p {
  margin: 0;
}

.run-match-inspector__decision h3 {
  color: var(--drama-color-text-muted);
  font-size: var(--drama-font-size-xs);
}

.run-match-inspector__decision p {
  line-height: 1.6;
}

.run-match-inspector__execution {
  padding: var(--drama-space-lg);
  border: 1px solid var(--drama-color-border);
  border-radius: var(--drama-radius-md);
  background: var(--drama-color-surface-alt);
}

.run-match-inspector__execution h3,
.run-match-inspector__execution p {
  margin: 0;
}

.run-match-inspector__execution p {
  line-height: 1.65;
}

.run-match-inspector__conversations h3 {
  font-size: var(--drama-font-size-sm);
}

.run-match-inspector__conversations a,
.run-match-inspector__link {
  display: inline-flex;
  align-items: center;
  gap: var(--drama-space-xs);
  width: fit-content;
  color: var(--drama-color-primary);
  text-decoration: none;
}

.run-match-inspector__candidates {
  grid-template-columns: repeat(auto-fit, minmax(280px, 1fr));
}

.run-match-inspector__embedding {
  grid-template-columns: repeat(2, minmax(0, 1fr));
  padding-top: var(--drama-space-lg);
  border-top: 1px solid var(--drama-color-border);
}

.run-match-inspector__embedding > div {
  display: grid;
  align-content: start;
  gap: var(--drama-space-md);
}

.run-match-inspector__embedding h3,
.run-match-inspector__embedding p,
.run-match-inspector__agent-calls h3,
.run-match-inspector__agent-calls p {
  margin: 0;
}

.run-match-inspector__embedding p,
.run-match-inspector__agent-calls > header p,
.run-match-inspector__empty {
  color: var(--drama-color-text-muted);
  font-size: var(--drama-font-size-sm);
}

.run-match-inspector__no-node-logs {
  margin: 0;
  padding: var(--drama-space-lg);
  border: 1px solid var(--drama-color-border);
  border-radius: var(--drama-radius-md);
  background: var(--drama-color-surface-alt);
  color: var(--drama-color-text-muted);
}

.run-match-inspector__agent-calls {
  padding-top: var(--drama-space-lg);
  border-top: 1px solid var(--drama-color-border);
}

.run-match-inspector__agent-calls > header,
.run-match-inspector__agent-conversation > header {
  display: flex;
  align-items: flex-start;
  justify-content: space-between;
  gap: var(--drama-space-md);
}

.run-match-inspector__agent-conversation {
  padding: var(--drama-space-lg);
  border: 1px solid var(--drama-color-border);
  border-radius: var(--drama-radius-md);
  background: var(--drama-color-surface-alt);
}

.run-match-inspector__agent-conversation code {
  color: var(--drama-color-text-muted);
}

.run-match-inspector code {
  font-family: var(--drama-font-family-mono);
  font-size: var(--drama-font-size-xs);
  overflow-wrap: anywhere;
}

@media (max-width: 980px) {
  .run-match-inspector__embedding {
    grid-template-columns: 1fr;
  }
}
</style>
